import {
  attr,
  computedOf,
  html,
  MapSignal,
  on,
  WithElement,
} from '@tempots/dom'
import { renderDesignToHtml } from '@rh/shared/templates/design-renderer'
import { convertCustomizeToDesign } from '@rh/shared/templates/customize-to-design'
import { PAGE_SIZES } from '@rh/shared/schemas/design'
import { AgencyAssetService } from '@/services/agency-asset-service'
import {
  buildFormCustomizeData,
  wizardTokenData,
  type HeaderShape,
  type WizardState,
} from '../wizard-state'

// Labels describe the arrangement rather than counting columns — the
// legacy shape names count TEXT columns, but a logo adds one more visual
// column, so "Three columns" reads as four to anyone with a logo.
const SHAPES: Array<{ shape: HeaderShape; label: string }> = [
  { shape: 'columns-3', label: 'Spread across the top' },
  { shape: 'columns-2', label: 'Two groups' },
  { shape: 'columns-1-text', label: 'One block on the left' },
  { shape: 'columns-1-image', label: 'Logo on top, details below' },
]

// Thumbnails crop to the top of the page so the header band dominates.
const THUMB_CROP_H = 170

export function ShapeStep(state: WizardState, agencyId: string) {
  const resolveImageUrl = (assetKey: string) =>
    AgencyAssetService.getDesignImageAssetUrl(agencyId, assetKey)

  // Re-render thumbnails when the async recruiter profile lands (tokens
  // resolve to real values) or the selection changes.
  const thumbInputs = computedOf(
    state.profile,
    state.shape
  )((profile, selected) => ({ profile, selected }))

  return html.div(
    html.div(
      attr.class('wiz-callout'),
      html.b('Nothing here locks you in.'),
      ' This is just a starting point — you can move, restyle, or change every part of it in the editor afterwards.'
    ),
    MapSignal(thumbInputs, ({ selected }) =>
      html.div(
        attr.class('wiz-shapes'),
        ...SHAPES.map(({ shape, label }) => {
          // Render this shape with the user's current values so the choice
          // is between REAL previews, not abstract diagrams.
          const data = buildFormCustomizeData(state)
          const design = convertCustomizeToDesign(
            {
              ...data,
              header: { ...data.header, shape },
              includeFooter: false,
            },
            { headerLogoAssetKey: state.logoAssetKey.value }
          )
          const rendered = renderDesignToHtml(design, 'first', {
            tokenData: wizardTokenData(state),
            resolveImageUrl,
          })
          return html.button(
            attr.type('button'),
            attr.class(shape === selected ? 'wiz-shape selected' : 'wiz-shape'),
            on.click(() => {
              state.shape.set(shape)
              state.step.set('info')
            }),
            html.div(
              attr.class('wiz-shape-thumb'),
              ScaledCrop(PAGE_SIZES.letter.w, THUMB_CROP_H, rendered.html)
            ),
            html.div(attr.class('wiz-shape-label'), label)
          )
        })
      )
    ),
    html.p(
      attr.class('wiz-hint'),
      attr.style('margin-top:12px;'),
      'Click a layout to continue — you’ll fill in your details next.',
      MapSignal(state.logoAssetKey, key =>
        key
          ? html.span(
              ' These previews use your saved agency logo — you can replace it on the next step.'
            )
          : html.span()
      ),
      ' Colors can be changed on the next step too.'
    )
  )
}

/**
 * A page-width HTML fragment scaled down into a fixed-height thumbnail
 * crop. The scale is computed from the tile's measured width one frame
 * after mount (grid tracks aren't laid out at render time).
 */
function ScaledCrop(naturalW: number, cropH: number, innerHtml: string) {
  return html.div(
    attr.style(
      `position:relative; width:100%; height:${cropH}px; overflow:hidden;`
    ),
    html.div(
      attr.style(
        `width:${naturalW}px; height:${cropH * 4}px; transform-origin:top left; position:absolute; top:0; left:0; background:white;`
      ),
      attr.innerHTML(innerHtml),
      WithElement(el => {
        requestAnimationFrame(() => {
          const parent = el.parentElement
          if (!parent) return
          const w = parent.clientWidth
          if (w > 0) el.style.transform = `scale(${w / naturalW})`
        })
      })
    )
  )
}
